import { useState, useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  TrashIcon,
  PencilSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationCircleIcon,
  CheckIcon,
  PlusIcon,
  Square2StackIcon,
} from '@heroicons/react/24/outline';
import { Button, cn } from '@pantry/ui';
import type { BatchDraftItem, BatchDraftItemStatus } from '@pantry/shared';
import {
  validateBatchDraftItem,
  getTotalInstanceQuantity,
  UNIT_DISPLAY_LABELS,
} from '@pantry/shared';
import { BatchInstanceRow } from './BatchInstanceRow';

interface GroupedBatchItemCardProps {
  /** The draft item to display */
  item: BatchDraftItem;
  /** Whether multi-select mode is active */
  isMultiSelectMode: boolean;
  /** Whether this item is selected (in multi-select mode) */
  isSelected: boolean;
  /** Called when card is clicked in multi-select mode */
  onToggleSelect: () => void;
  /** Called when the edit button is pressed (opens edit sheet for item) */
  onEdit: () => void;
  /** Called when the item should be deleted */
  onDelete: () => void;
  /** Called when an instance's edit button is pressed */
  onEditInstance: (instanceId: string) => void;
  /** Called when an instance should be deleted */
  onDeleteInstance: (instanceId: string) => void;
  /** Called when an instance's quantity changes */
  onUpdateInstanceQuantity: (instanceId: string, quantity: number) => void;
  /** Called when "Add Batch" is pressed */
  onAddBatch: () => void;
}

/**
 * Format a date string for display using user's locale (e.g., "Dec 15").
 */
function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Expandable card component for batch entry.
 * Shows product header with total quantity.
 * Can expand to show individual instances with their expiry dates.
 * Supports multi-select mode with checkbox.
 */
export function GroupedBatchItemCard({
  item,
  isMultiSelectMode,
  isSelected,
  onToggleSelect,
  onEdit,
  onDelete,
  onEditInstance,
  onDeleteInstance,
  onUpdateInstanceQuantity,
  onAddBatch,
}: GroupedBatchItemCardProps) {
  const { t, i18n } = useLingui();
  const [isExpanded, setIsExpanded] = useState(false);

  const validationStatus: BatchDraftItemStatus = validateBatchDraftItem(item);
  const isInvalid = validationStatus !== 'valid';
  const totalQuantity = getTotalInstanceQuantity(item);
  const instanceCount = item.instances?.length ?? 1;
  const hasMultipleInstances = instanceCount > 1;

  // Get earliest expiry date for header display
  const earliestExpiry = item.instances
    ?.filter((i) => i.expiryDate)
    .sort((a, b) => (a.expiryDate! < b.expiryDate! ? -1 : 1))[0]?.expiryDate;

  const handleCardClick = useCallback(() => {
    if (isMultiSelectMode) {
      onToggleSelect();
    } else if (hasMultipleInstances) {
      setIsExpanded(!isExpanded);
    }
  }, [isMultiSelectMode, hasMultipleInstances, isExpanded, onToggleSelect]);

  const unitLabel = UNIT_DISPLAY_LABELS[item.unit] || t`item`;

  return (
    <div
      className={cn(
        'relative rounded-xl border transition-all animate-in fade-in slide-in-from-top-2 duration-200 overflow-hidden',
        isInvalid
          ? 'border-expiring-soon bg-expiring-soon/5'
          : 'border-border/50 bg-card hover:border-border',
        isSelected && 'border-primary border-2 bg-primary/5'
      )}
    >
      {/* Main card content */}
      <div
        className={cn(
          'flex items-start gap-3 p-3',
          (hasMultipleInstances || isMultiSelectMode) && 'cursor-pointer'
        )}
        onClick={handleCardClick}
      >
        {/* Selection checkbox (multi-select mode) */}
        {isMultiSelectMode && (
          <div
            className={cn(
              'flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors',
              isSelected
                ? 'border-primary bg-primary'
                : 'border-border bg-transparent hover:border-primary/50'
            )}
          >
            {isSelected && <CheckIcon className="h-3.5 w-3.5 text-primary-foreground" />}
          </div>
        )}

        {/* Product Image */}
        <div className="flex-shrink-0 h-14 w-14 rounded-lg overflow-hidden bg-muted">
          {item.displayImageUrl ? (
            <img
              src={item.displayImageUrl}
              alt={item.displayName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-muted-foreground text-xs">
              <Trans>No img</Trans>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Name & Brand */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{item.displayName}</p>
              {item.displayBrand && (
                <p className="text-xs text-muted-foreground truncate">{item.displayBrand}</p>
              )}
            </div>

            {/* Actions (only show when not in multi-select) */}
            {!isMultiSelectMode && (
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Expand button for multiple instances */}
                {hasMultipleInstances && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 rounded-lg hover:bg-muted"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(!isExpanded);
                    }}
                  >
                    {isExpanded ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-lg hover:bg-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  title={t`Edit item`}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  title={t`Remove item`}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Details Row */}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {/* Quantity badge */}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
              {totalQuantity} {unitLabel !== 'item' ? unitLabel : ''}
            </span>

            {/* Instance count badge (if multiple) */}
            {hasMultipleInstances && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">
                <Square2StackIcon className="h-3 w-3" />
                <Trans>{instanceCount} batches</Trans>
              </span>
            )}

            {/* Expiry badge */}
            {earliestExpiry && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                <Trans>Exp: {formatDate(earliestExpiry, i18n.locale)}</Trans>
              </span>
            )}

            {/* External badge */}
            {item.isExternal && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                <Trans>New</Trans>
              </span>
            )}
          </div>

          {/* Validation Warning */}
          {isInvalid && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-expiring-soon">
              <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
              <span>
                {validationStatus === 'needs_input' ? (
                  <Trans>Needs product name</Trans>
                ) : (
                  <Trans>Invalid item</Trans>
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Expanded instances */}
      {isExpanded && !isMultiSelectMode && item.instances && (
        <div className="border-t border-border/50">
          {/* Instance list */}
          {item.instances.map((instance, idx) => (
            <BatchInstanceRow
              key={instance.instanceId}
              instance={instance}
              index={idx}
              onEdit={() => onEditInstance(instance.instanceId)}
              onDelete={() => onDeleteInstance(instance.instanceId)}
              onQuantityUp={() =>
                onUpdateInstanceQuantity(instance.instanceId, instance.quantity + 1)
              }
              onQuantityDown={() =>
                onUpdateInstanceQuantity(instance.instanceId, Math.max(1, instance.quantity - 1))
              }
            />
          ))}

          {/* Add batch button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddBatch();
            }}
            className="w-full flex items-center justify-center gap-1.5 py-3 bg-muted/50 hover:bg-muted transition-colors rounded-b-xl"
          >
            <PlusIcon className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              <Trans>Add Another Batch</Trans>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
